import React from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';

import { Component, RelativeBearing } from '@paragon/contract-library';
import { DistanceField } from '@paragon/material';
import { InlineNotification } from '@paragon/notification-tools';
import { findAndCalculateLine } from '@paragon/relative-geometry';

import {
  setBearingLineLocation,
  setBearingReverseDirection,
  setBearingWidthRelativeGeometry,
  switchBearingRelativeGeometryType,
} from '../../../../store/actions/editBearingActions';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import BearingReferenceLinePicker from './BearingReferenceLinePicker';
import { LineLocationSelect } from './LineLocationSelect';

interface Props {
  readonly bearing: RelativeBearing;
  readonly component: Component;
}

const BearingRelativeGeometrySubPanel = ({ bearing, component }: Props) => {
  const dispatch = useAppDispatch();
  const activeComponentDesignGuid = useAppSelector((state) => state.selections.activeComponentDesignGuid);
  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);

  const onApplyWidth = (number: number) =>
    dispatch(setBearingWidthRelativeGeometry(bearing, activeComponentDesignGuid!, number));

  const { relativeGeometry } = bearing;
  if (!relativeGeometry) {
    console.error('Bearing does not have relative geometry.');
    return null;
  }

  let showParallelBearingDefinitionLinesWarning = false;
  if (relativeGeometry.bearingGeometryType === 'Bearing') {
    const line = findAndCalculateLine(relativeGeometry.lineGuid, component);
    const bearingLine = findAndCalculateLine(relativeGeometry.bearingLineGuid, component);
    showParallelBearingDefinitionLinesWarning = !!line && !!bearingLine && line.isParallelTo(bearingLine);
  }

  return (
    <Box>
      <Box sx={{ m: 1 }}>
        <FormControlLabel
          label="Continuous bearing"
          control={
            <Checkbox
              checked={bearing.relativeGeometry?.bearingGeometryType === 'ContinuousBearing'}
              onChange={() =>
                void dispatch(
                  switchBearingRelativeGeometryType(
                    bearing,
                    activeComponentDesignGuid!,
                    bearing.relativeGeometry?.bearingGeometryType === 'Bearing' ? 'ContinuousBearing' : 'Bearing',
                  ),
                )
              }
              disabled={isViewOnly || !activeComponentDesignGuid || !bearing.relativeGeometry}
            />
          }
        />
      </Box>
      {relativeGeometry.bearingGeometryType === 'ContinuousBearing' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <BearingReferenceLinePicker elevation={1} label="Left Side Reference Line" propertyName="leftSideLineGuid" />
          <BearingReferenceLinePicker
            elevation={1}
            label="Right Side Reference Line"
            propertyName="rightSideLineGuid"
          />
        </Box>
      ) : (
        <Box>
          <DistanceField
            sx={{ m: 1 }}
            label="Width"
            value={relativeGeometry.width}
            min={0}
            onSubmit={onApplyWidth}
            disabled={isViewOnly}
          />

          <LineLocationSelect
            relativeGeometry={relativeGeometry}
            submit={(lineLocation) => {
              dispatch(setBearingLineLocation(bearing, activeComponentDesignGuid!, lineLocation));
            }}
            disabled={isViewOnly}
          />

          {showParallelBearingDefinitionLinesWarning && (
            <InlineNotification type="warning">Lines should not be parallel</InlineNotification>
          )}

          <BearingReferenceLinePicker elevation={1} label="Definition Reference Line" propertyName="lineGuid" />
        </Box>
      )}
      <BearingReferenceLinePicker elevation={1} label="Bearing Reference Line" propertyName="bearingLineGuid" />
      <Box sx={{ m: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={relativeGeometry.directionReversed}
              onChange={(event) => {
                dispatch(setBearingReverseDirection(bearing, activeComponentDesignGuid!, event.target.checked));
              }}
            />
          }
          label="Bearing Flipped"
          disabled={isViewOnly}
        />
      </Box>
    </Box>
  );
};

export default BearingRelativeGeometrySubPanel;
